<template>
  <b-modal
    id="review-fast-invoice-modal"
    ref="review-fast-invoice-modal"
    size="xl"
    dialog-class="review-fast-modal-dialog"
    hide-footer
    no-close-on-backdrop
    :title="modalTitle"
    @hidden="onHidden"
  >
    <b-overlay
      :show="loading"
      variant="white"
      opacity="0.85"
      rounded="sm"
    >
      <div v-if="rows.length">
        <div
          class="d-flex justify-content-between align-items-center flex-wrap mb-3"
        >
          <div class="d-flex align-items-center flex-wrap">
            <span class="mr-3">Tổng: <strong>{{ rows.length }}</strong></span>
            <span class="mr-3 text-success"
              >Hợp lệ: <strong>{{ validRows.length }}</strong></span
            >
            <span class="mr-3 text-danger"
              >Không hợp lệ: <strong>{{ invalidRows.length }}</strong></span
            >
            <VatNameFilter
              v-model="vatNameFilter"
              :options="vatNameOptions"
            />
          </div>
          <div class="d-flex align-items-center">
            <label class="mb-0 mr-2 font-weight-bold small">
              Nguồn tra cứu MST:
            </label>
            <b-form-radio-group
              v-model="fromPartner"
              :options="fromPartnerOptions"
              :disabled="submitting"
              size="sm"
              name="batch-tax-source"
            />
          </div>
          <div v-if="!isCreateMode" class="d-flex align-items-center">
            <label class="mb-0 mr-2 font-weight-bold small">Chế độ:</label>
            <b-form-radio-group
              v-model="editMode"
              :options="editModeOptions"
              :disabled="submitting"
              size="sm"
              name="publish-edit-mode"
              buttons
              button-variant="outline-primary"
            />
          </div>
          <div v-if="hasResults">
            <span class="mr-3 text-success"
              >Thành công: <strong>{{ successCount }}</strong></span
            >
            <span class="text-danger"
              >Thất bại: <strong>{{ failedCount }}</strong></span
            >
          </div>
        </div>

        <div v-if="isFiltering" class="rf-filter-bar">
          <span>
            <i class="fas fa-filter mr-1" aria-hidden="true"></i>
            Đang lọc
            <strong>{{ filteredRows.length }}</strong>/{{ rows.length }} hóa đơn
          </span>
          <button type="button" class="rf-filter-clear" @click="clearAllFilters">
            Xóa tất cả lọc
          </button>
          <span class="rf-filter-note">
            Bấm {{ confirmLabel }} chỉ xử lý các hóa đơn đang hiển thị.<template
              v-if="isCreateMode"
            >
              Bỏ chọn cột "Xuất" để loại dòng.</template
            ><template v-else>
              Bỏ chọn dòng 0đ ở cột "Xuất" để loại khỏi hóa đơn đỏ.</template
            >
          </span>
        </div>

        <b-alert
          :show="emailInvalidCount > 0"
          variant="warning"
          class="py-1 px-2 mb-3 small"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          <strong>{{ emailInvalidCount }}</strong> hóa đơn có email chưa hợp lệ.
          B2B bắt buộc nhập email đúng định dạng; B2C không bắt buộc nhưng nếu
          nhập phải đúng định dạng. Nhiều email cách nhau bởi dấu "<strong>;</strong>".
        </b-alert>

        <div
          v-for="group in suggestionGroups"
          :key="`suggest-${group.field}`"
          class="rf-suggest-bar"
        >
          <span class="rf-suggest-text">
            <i class="fas fa-lightbulb mr-1" aria-hidden="true"></i>
            <strong>{{ group.rows.length }}</strong> hóa đơn
            <template v-if="isFiltering">đang lọc </template>có đề xuất chuẩn hóa
            {{ group.label }} — xem dòng "Đề xuất" dưới từng ô.
          </span>
          <b-button
            variant="primary"
            size="sm"
            class="font-weight-bolder rf-suggest-apply"
            :disabled="submitting"
            @click="applySuggestionGroup(group)"
          >
            Áp dụng {{ group.label }} ({{ group.rows.length }})
          </b-button>
        </div>

        <div v-if="zeroBulk.details.length" class="rf-suggest-bar">
          <span class="rf-suggest-text">
            <i class="fas fa-box-open mr-1" aria-hidden="true"></i>
            <strong>{{ zeroBulk.details.length }}</strong> dòng 0đ trong
            <strong>{{ zeroBulk.invoiceCount }}</strong> hóa đơn
            <template v-if="isFiltering">đang lọc</template
            ><template v-if="vatNameFilter.length">
              (chỉ tên xuất VAT đã chọn)</template
            ><template v-if="zeroBulk.excludedCount">
              · đã bỏ chọn <strong>{{ zeroBulk.excludedCount }}</strong></template
            >. Kiểm tra tồn kho bản thuế trên FAST trước khi loại.
          </span>
          <b-button
            variant="outline-danger"
            size="sm"
            class="font-weight-bolder rf-suggest-apply"
            :disabled="submitting || !zeroBulk.includedCount"
            @click="bulkExcludeZeroDetails"
          >
            Bỏ chọn dòng 0đ ({{ zeroBulk.includedCount }})
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            class="font-weight-bolder rf-suggest-apply"
            :disabled="submitting || !zeroBulk.excludedCount"
            @click="setZeroDetailsInclude(true)"
          >
            Chọn lại ({{ zeroBulk.excludedCount }})
          </b-button>
        </div>

        <div class="table-responsive">
          <table
            class="table table-bordered table-sm review-fast-table"
          >
            <thead>
              <tr>
                <th
                  v-for="col in filterColumns"
                  :key="col.key"
                  class="rf-th"
                  :style="{ minWidth: col.minWidth }"
                >
                  <div class="rf-th-inner">
                    <span
                      class="rf-th-label"
                      :class="'is-' + col.align"
                    >{{ col.label }}</span>
                    <TableColumnFilter
                      class="rf-th-filter"
                      :label="col.label"
                      :options="optionsByColumn[col.key]"
                      v-model="filters[col.key]"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <!-- Mỗi hóa đơn = 1 <tbody>: dòng hóa đơn + bảng sản phẩm của nó thành 1 khối,
                 có vạch ngăn với hóa đơn kế tiếp (style .rf-bill-group). -->
            <tbody
              v-for="(row, rowIndex) in filteredRows"
              :key="row.billNumber"
              class="rf-bill-group"
              :class="{ 'is-expanded': isExpanded(row.billNumber) }"
            >
                <tr
                  :data-email-index="rowIndex"
                  class="rf-bill-row"
                  :class="{
                    'review-fast-error-row':
                      row.isFatal ||
                      row.resultStatus === RESULT_STATUS.FAILED ||
                      !!emailErrorFor(row),
                    'rf-email-fill-target': isEmailFillTarget(rowIndex),
                  }"
                >
                <td class="text-center align-middle">
                  <b-button
                    v-if="row.details && row.details.length"
                    variant="link"
                    size="sm"
                    class="p-0 mb-1 review-fast-expand-btn"
                    :title="
                      isExpanded(row.billNumber)
                        ? 'Thu gọn sản phẩm'
                        : 'Xem sản phẩm'
                    "
                    @click="toggleExpand(row.billNumber)"
                  >
                    <i
                      class="mr-1"
                      :class="
                        isExpanded(row.billNumber)
                          ? 'fas fa-chevron-down'
                          : 'fas fa-chevron-right'
                      "
                    ></i>{{ row.details.length }} SP
                  </b-button>
                  <div class="font-weight-bold">{{ row.billNumber }}</div>
                  <span
                    v-if="billTypeBadge"
                    class="label label-inline label-sm mt-1"
                    :class="billTypeBadge.class"
                    >{{ billTypeBadge.text }}</span
                  >
                </td>
                <td class="text-center align-middle">
                  <b-form-select
                    v-if="customerEditable"
                    v-model="row.billVatType"
                    size="sm"
                    :disabled="!isEditable(row)"
                    :options="vatTypeOptions"
                    title="Chọn loại hóa đơn: B2B (công ty) để nhập & tra cứu MST"
                  />
                  <span
                    v-else
                    class="label label-inline label-sm"
                    :class="
                      row.billVatType === VAT_TYPE.COMPANY
                        ? 'label-light-primary'
                        : 'label-light-info'
                    "
                    >{{
                      row.billVatType === VAT_TYPE.COMPANY ? 'B2B' : 'B2C'
                    }}</span
                  >
                </td>
                <!-- Khách hàng: 5 cột riêng ở CẢ 2 bước. Ô nhập khi Tạo hóa đơn đỏ hoặc Phát hành + "Chỉnh sửa";
                     Phát hành + "Xem" → chữ chỉ đọc. -->
                <td class="align-middle">
                  <template v-if="customerEditable">
                    <b-form-input
                      size="sm"
                      v-model="row.form.billVatName"
                      :disabled="!isEditable(row)"
                      placeholder="Tên khách hàng"
                    />
                    <div
                      v-if="
                        isCreateMode &&
                        isEditable(row) &&
                        suggestionFor(row, 'billVatName')
                      "
                      class="small text-warning mt-1 review-fast-suggest"
                    >
                      <i class="fas fa-lightbulb mr-1"></i>Đề xuất:
                      <strong>{{ suggestionFor(row, 'billVatName').to }}</strong>
                    </div>
                  </template>
                  <span v-else class="font-weight-bold">{{
                    row.form.billVatName || '-'
                  }}</span>
                </td>
                <td class="align-middle">
                  <b-input-group v-if="customerEditable" size="sm">
                    <b-form-input
                      v-model="row.form.taxCode"
                      :disabled="!isEditable(row)"
                      :placeholder="
                        row.billVatType === VAT_TYPE.COMPANY ? 'Nhập MST' : 'MST'
                      "
                      @input="onTaxCodeInput(row, $event)"
                    />
                    <b-input-group-append
                      v-if="row.billVatType === VAT_TYPE.COMPANY"
                    >
                      <b-button
                        variant="light-primary"
                        size="sm"
                        class="btn-icon rf-lookup-btn"
                        :disabled="!isEditable(row)"
                        title="Tra cứu & điền thông tin theo MST"
                        aria-label="Tra cứu MST"
                        @click="lookupTaxCode(row)"
                      >
                        <i class="flaticon2-search" aria-hidden="true"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <template v-else>{{ row.form.taxCode || '-' }}</template>
                  <div
                    v-for="(w, wi) in warningsFor(row, 'taxCode')"
                    :key="`w-tax-${row.billNumber}-${wi}`"
                    class="small text-warning mt-1"
                  >
                    <i class="fas fa-exclamation-triangle mr-1"></i>{{
                      w.message
                    }}
                  </div>
                </td>
                <td class="align-middle">
                  <b-form-input
                    v-if="customerEditable"
                    size="sm"
                    v-model="row.form.phone"
                    :disabled="!isEditable(row)"
                    placeholder="SĐT"
                  />
                  <template v-else>{{ row.form.phone || '-' }}</template>
                </td>
                <td class="align-middle">
                  <b-form-input
                    v-if="customerEditable"
                    size="sm"
                    v-model="row.form.billVatAddress"
                    :disabled="!isEditable(row)"
                  />
                  <span v-else class="text-break">{{
                    row.form.billVatAddress || '-'
                  }}</span>
                  <div
                    v-for="(w, wi) in warningsFor(row, 'billVatAddress')"
                    :key="`w-addr-${row.billNumber}-${wi}`"
                    class="small text-warning mt-1"
                  >
                    <i class="fas fa-exclamation-triangle mr-1"></i>{{
                      w.message
                    }}
                  </div>
                </td>
                <td
                  class="align-middle rf-email-cell"
                  :class="{ 'is-fillable': customerEditable && isEditable(row) }"
                  :title="
                    customerEditable
                      ? 'Kéo xuống để gán email này cho các dòng bên dưới'
                      : ''
                  "
                  @pointerdown="armEmailFill($event, rowIndex)"
                >
                  <template v-if="customerEditable">
                    <div class="rf-email-field">
                      <b-form-input
                        size="sm"
                        v-model="row.form.email"
                        :disabled="!isEditable(row)"
                        :state="emailErrorFor(row) ? false : null"
                        placeholder="Email, nhiều email cách nhau bởi ' ; '"
                      />
                    </div>
                    <div
                      v-if="emailErrorFor(row)"
                      class="small text-danger mt-1"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>{{
                        emailErrorFor(row)
                      }}
                    </div>
                  </template>
                  <span v-else class="text-break">{{
                    row.form.email || '-'
                  }}</span>
                </td>
                <td class="align-middle">
                  <template v-if="rowCheckTags(row).length">
                    <span
                      v-for="tag in rowCheckTags(row)"
                      :key="`ck-${row.billNumber}-${tag}`"
                      v-b-tooltip.hover
                      class="label label-inline label-sm mr-1 mb-1"
                      :class="CHECK_TAG_META[tag].class"
                      :title="CHECK_TAG_META[tag].hint"
                      >{{ tag }}</span
                    >
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="align-middle">
                  <template v-if="row.resultStatus">
                    <div
                      v-if="row.resultStatus === RESULT_STATUS.SUCCESS"
                      class="text-success font-weight-bold"
                    >
                      <i class="fas fa-check-circle mr-1"></i>{{
                        successRowLabel
                      }}
                      <div
                        v-if="isCreateMode && row.billVatId"
                        class="small"
                      >
                        Mã hóa đơn đỏ: {{ row.billVatId }}
                      </div>
                      <div
                        v-else-if="!isCreateMode && row.fastInvoiceNo"
                        class="small"
                      >
                        Số HĐ FAST: {{ row.fastInvoiceNo }}
                      </div>
                    </div>
                    <div v-else class="text-danger font-weight-bold">
                      <i class="fas fa-times-circle mr-1"></i>Thất bại
                      <div v-if="row.error" class="small font-weight-normal">
                        {{ row.error }}
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div v-if="row.errors.length">
                      <div
                        v-for="(err, ei) in row.errors"
                        :key="`e-${row.billNumber}-${ei}`"
                        class="text-danger small"
                      >
                        <i class="fas fa-exclamation-circle mr-1"></i>{{
                          err.message
                        }}
                      </div>
                    </div>
                    <!-- Lỗi email client-side (B2B bắt buộc nhập / sai định dạng) CŨNG là KHÔNG hợp lệ:
                         phải phản ánh vào cột trạng thái, không để "Hợp lệ" trong khi vẫn chặn submit. -->
                    <div v-if="emailErrorFor(row)" class="text-danger small">
                      <i class="fas fa-exclamation-circle mr-1"></i>{{
                        emailErrorFor(row)
                      }}
                    </div>
                    <div
                      v-if="!row.errors.length && !emailErrorFor(row)"
                      class="text-success small font-weight-bold"
                    >
                      <i class="fas fa-check-circle mr-1"></i>Hợp lệ
                    </div>
                    <div v-if="row.warnings.length" class="mt-1">
                      <div
                        v-for="(w, wi) in row.warnings"
                        :key="`w-${row.billNumber}-${wi}`"
                        class="text-warning small"
                      >
                        <i class="fas fa-exclamation-triangle mr-1"></i>{{
                          w.message
                        }}
                      </div>
                    </div>
                  </template>
                </td>
                </tr>
                <tr
                  v-if="isExpanded(row.billNumber)"
                  class="review-fast-detail-row"
                >
                  <td :colspan="detailColspan" class="p-0">
                    <div class="rf-detail-panel">
                      <div v-if="row.returnSummary" class="rf-return-summary mb-3">
                        <div class="font-weight-bold small mb-2 text-dark-75">
                          <i class="fas fa-undo-alt mr-1" aria-hidden="true"></i>Đối chiếu
                          trả hàng — phiếu trả #{{
                            row.returnSummary.refundBillNumbers.join(', #')
                          }}
                        </div>
                        <table class="table table-bordered table-sm mb-0 rf-return-table">
                          <thead>
                            <tr>
                              <th>Sản phẩm</th>
                              <th class="text-center rf-return-qty">SL bán</th>
                              <th class="text-center rf-return-qty">SL trả</th>
                              <th class="text-center rf-return-qty">Còn lại</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="p in row.returnSummary.products"
                              :key="`rs-${row.billNumber}-${p.productId}`"
                              :class="{ 'rf-return-hit': p.returnedQuantity > 0 }"
                            >
                              <td>
                                {{ p.productName || `SP #${p.productId}` }}
                                <span v-if="p.productCode" class="text-muted"
                                  >({{ p.productCode }})</span
                                >
                                <div
                                  v-if="p.returnedImeis.length"
                                  class="text-muted text-break"
                                >
                                  IMEI đã trả: {{ p.returnedImeis.join(', ') }}
                                </div>
                                <div v-if="p.keptGiftQuantity" class="text-muted">
                                  Khách giữ lại quà: {{ p.keptGiftQuantity }}
                                </div>
                              </td>
                              <td class="text-center">{{ p.soldQuantity }}</td>
                              <td class="text-center">{{ p.returnedQuantity }}</td>
                              <td class="text-center">{{ remainingQuantity(p) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="rf-detail-title">
                        <i
                          class="fas fa-level-up-alt fa-rotate-90 mr-2"
                          aria-hidden="true"
                        ></i
                        >Sản phẩm của hóa đơn
                        <span class="rf-detail-bill">#{{ row.billNumber }}</span>
                        <span class="rf-detail-count"
                          >· {{ row.details.length }} sản phẩm</span
                        >
                        <span
                          v-if="!isCreateMode && excludedZeroCount(row)"
                          class="rf-detail-excluded"
                          >· loại {{ excludedZeroCount(row) }} dòng 0đ khi phát hành</span
                        >
                      </div>
                      <table
                        class="table table-bordered table-sm mb-0 review-fast-detail-table"
                      >
                        <thead>
                          <tr>
                            <th
                              v-b-tooltip.hover
                              class="text-center"
                              style="min-width: 60px"
                              :title="includeColumnHint"
                            >
                              Xuất
                            </th>
                            <th class="text-center" style="min-width: 32px">STT</th>
                            <th style="min-width: 120px">Mã SP</th>
                            <th class="col-product-name">Tên sản phẩm</th>
                            <th class="text-center" style="min-width: 34px">SL</th>
                            <th
                              class="text-center"
                              style="min-width: 42px; white-space: normal"
                            >
                              Thuế suất
                            </th>
                            <th class="text-right" style="min-width: 84px">Giá</th>
                            <th
                              class="text-right"
                              style="min-width: 72px; white-space: normal"
                            >
                              Chiết khấu
                            </th>
                            <th class="text-right" style="min-width: 120px">
                              Thành tiền
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(d, di) in row.details"
                            :key="`d-${row.billNumber}-${di}`"
                            :class="{ 'text-muted': !d.include }"
                          >
                            <td
                              v-b-tooltip.hover
                              class="text-center"
                              :title="detailIncludeTooltip(d)"
                            >
                              <b-form-checkbox
                                v-model="d.include"
                                :disabled="isIncludeLocked(row, d)"
                                class="d-inline-block"
                              />
                            </td>
                            <td class="text-center">{{ di + 1 }}</td>
                            <td>{{ d.productCode || '-' }}</td>
                            <td class="col-product-name">
                              <i
                                v-if="isDetailInvalid(d)"
                                v-b-tooltip.hover
                                :title="detailInvalidReason(d)"
                                class="fa fa-exclamation-triangle text-danger mr-1"
                              ></i>
                              {{ d.productName || '-' }}
                              <span
                                v-if="d.isImei"
                                class="label label-inline label-sm label-light-primary ml-1"
                                >IMEI</span
                              >
                              <span
                                v-if="d.vatNameConverted"
                                v-b-tooltip.hover
                                class="label label-inline label-sm label-light-success ml-1"
                                title="Sản phẩm Samsung đã kích hoạt — tự chuyển về SẢN PHẨM GỐC khi xuất VAT"
                                >→ SP gốc</span
                              >
                              <span
                                v-if="d.vatOriginalNotFound"
                                v-b-tooltip.hover
                                class="label label-inline label-sm label-light-danger ml-1"
                                title="Sản phẩm Samsung 'Đã Kích hoạt' nhưng CHƯA tra được SP gốc theo IMEI — tên xuất VAT có thể còn 'Đã Kích hoạt'. Kiểm tra lại phiếu đổi mã."
                                >SP gốc?</span
                              >
                              <span
                                v-if="d.isZeroValue"
                                v-b-tooltip.hover
                                class="label label-inline label-sm label-light-warning ml-1"
                                :title="zeroValueHint(d)"
                                >Quà tặng 0đ</span
                              >
                              <span
                                v-if="d.isProductInactive"
                                v-b-tooltip.hover
                                class="label label-inline label-sm label-light-danger ml-1"
                                :title="`Mã đẩy FAST không còn hiệu lực${d.productInactiveReason ? ` (${d.productInactiveReason})` : ''} — cập nhật sản phẩm/mã VAT trước khi xuất`"
                                >Mã hết hiệu lực</span
                              >
                              <span
                                v-if="d.returnState === RETURN_STATE.FULL"
                                v-b-tooltip.hover
                                class="label label-inline label-sm label-light-dark ml-1"
                                title="Sản phẩm đã được khách trả hàng — không xuất hóa đơn cho dòng này"
                                >Đã trả</span
                              >
                              <span
                                v-else-if="d.returnState === RETURN_STATE.PARTIAL"
                                v-b-tooltip.hover
                                class="label label-inline label-sm label-light-warning ml-1"
                                title="Sản phẩm đã trả một phần — xem bảng đối chiếu, kiểm tra SL trước khi xuất"
                                >SP trả {{ d.productReturnedQuantity }}/{{
                                  d.productSoldQuantity
                                }}</span
                              >
                              <span
                                v-if="d.vatParentNotFound"
                                v-b-tooltip.hover
                                class="label label-inline label-sm label-light-warning ml-1"
                                title="Mã VAT của sản phẩm trỏ tới sản phẩm không tồn tại — FAST sẽ nhận mã sản phẩm con"
                                >Thiếu SP cha VAT</span
                              >
                              <span
                                v-if="d.hasVatName === false"
                                class="label label-inline label-sm label-light-danger ml-1"
                                >Thiếu tên VAT</span
                              >
                              <div
                                v-if="d.vatDisplayName && d.vatDisplayName !== (d.productName || '')"
                                class="small text-muted mt-1 text-break"
                              >
                                <i class="fas fa-file-invoice mr-1" aria-hidden="true"></i
                                >Tên xuất VAT: {{ d.vatDisplayName }}
                              </div>
                            </td>
                            <td class="text-center">{{ d.quantity }}</td>
                            <td class="text-center">{{ detailVatLabel(d) }}</td>
                            <td class="text-right">
                              {{ formatMoney(d.productPrice) }}
                            </td>
                            <td class="text-right">
                              {{ formatMoney(detailDiscount(d)) }}
                            </td>
                            <td class="text-right">
                              {{ formatMoney(lineNetAmount(d)) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div class="rf-detail-summary">
                        <div class="rf-sum-row">
                          <span>Tiền hàng</span>
                          <span>{{ formatMoney(rowSummary(row).grossAmount) }}</span>
                        </div>
                        <div class="rf-sum-row">
                          <span>Chiết khấu sản phẩm</span>
                          <span>{{ formatMoney(-rowSummary(row).productDiscount) }}</span>
                        </div>
                        <div
                          v-b-tooltip.hover
                          class="rf-sum-row"
                          title="Chiết khấu tổng đơn, tiêu điểm, thu lại quà tặng — chia xuống từng dòng theo tỷ lệ thành tiền"
                        >
                          <span>Chiết khấu tổng đơn (phân bổ)</span>
                          <span>{{ formatMoney(-rowSummary(row).allotmentDiscount) }}</span>
                        </div>
                        <div class="rf-sum-row rf-sum-total">
                          <span>Thành tiền xuất hóa đơn</span>
                          <span>{{ formatMoney(rowSummary(row).netAmount) }}</span>
                        </div>
                        <div class="rf-sum-note">
                          Tính trên {{ rowSummary(row).includedCount }}/{{
                            row.details.length
                          }}
                          dòng được xuất
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
            </tbody>
            <tbody v-if="!filteredRows.length">
              <tr>
                <td
                  :colspan="detailColspan"
                  class="text-center text-muted py-3"
                >
                  Không có dòng khớp bộ lọc
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else-if="!loading" class="text-center text-muted py-5">
        Không có dữ liệu để hiển thị.
      </div>

      <div class="rf-actions">
        <b-button
          variant="secondary"
          size="sm"
          class="font-weight-bolder"
          @click="hide"
        >
          {{ hasResults ? 'Thoát' : 'Đóng' }}
        </b-button>
        <b-button
          v-if="isCreateMode && successCount > 0"
          variant="success"
          size="sm"
          class="font-weight-bolder"
          @click="goToVatInvoiceList"
        >
          <i class="fas fa-external-link-alt" aria-hidden="true"></i>Xem danh sách hóa đơn đỏ
        </b-button>
        <b-button
          v-if="pendingRows.length > 0"
          variant="primary"
          size="sm"
          class="font-weight-bolder"
          :disabled="!canSubmit"
          @click="onSubmitBatch"
        >
          <b-spinner v-if="submitting" small class="mr-1" />
          {{ hasResults ? 'Thử lại' : confirmLabel }} ({{ submitCountLabel }})
        </b-button>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import debounce from 'debounce';

import ApiService from '@/core/services/api.service';
import { TIME_TRIGGER } from '@/utils/constants';
import {
  xoa_dau,
  makeToastSuccess,
  makeToastFaile,
  makeToastWarning,
  convertPrice,
} from '@/utils/common';
import { BILL_TYPE } from '@/utils/enum';
import { isValidEmail } from '@/utils/validation';
import TableColumnFilter from '@/view/components/common/TableColumnFilter.vue';
import VatNameFilter from '@/view/components/common/VatNameFilter.vue';

const VAT_TYPE = Object.freeze({ COMPANY: 1, INDIVIDUAL: 2 });
// Tình trạng trả hàng (BE: BILL_VAT_RETURN_STATE) — t101269.
const RETURN_STATE = Object.freeze({ NONE: 'NONE', PARTIAL: 'PARTIAL', FULL: 'FULL' });
// Nhãn cột "Kiểm tra" (t101269) — dùng CHUNG cho hiển thị, option lọc và so khớp lọc.
const CHECK_TAG = Object.freeze({
  RETURN_FULL: 'Trả toàn bộ',
  RETURN_PARTIAL: 'Trả một phần',
  INACTIVE: 'Mã hết hiệu lực',
  ZERO: 'Dòng 0đ',
  PARENT_MISSING: 'Thiếu SP cha VAT',
  NONE: 'Không vấn đề',
});
const CHECK_TAG_META = Object.freeze({
  [CHECK_TAG.RETURN_FULL]: {
    class: 'label-light-danger',
    hint: 'Khách đã trả toàn bộ sản phẩm — không xuất hóa đơn',
  },
  [CHECK_TAG.RETURN_PARTIAL]: {
    class: 'label-light-warning',
    hint: 'Khách đã trả một phần — dòng đã trả không được xuất',
  },
  [CHECK_TAG.INACTIVE]: {
    class: 'label-light-danger',
    hint: 'Có mã sản phẩm không còn hiệu lực trên FAST (ngừng bán / hết hàng / đã xóa)',
  },
  [CHECK_TAG.ZERO]: {
    class: 'label-light-warning',
    hint: 'Có dòng 0đ — kiểm tra tồn kho bản thuế trên FAST, bỏ chọn dòng không còn tồn',
  },
  [CHECK_TAG.PARENT_MISSING]: {
    class: 'label-light-primary',
    hint: 'Có sản phẩm mà mã VAT trỏ tới sản phẩm không tồn tại',
  },
});
const RESULT_STATUS = Object.freeze({ SUCCESS: 'SUCCESS', FAILED: 'FAILED' });
// Field BE có thể đề xuất làm sạch (RULE-002/003) + nhãn nút "Áp dụng" hàng loạt.
const SUGGESTION_FIELDS = Object.freeze([
  { field: 'billVatName', label: 'tên khách hàng' },
]);
// Giới hạn độ dài chuỗi email (đồng bộ với luồng tạo single ở BillVatInvoiceInfo.vue).
const MAX_EMAIL_LENGTH = 150;
// Phần local (trước dấu '@') tối thiểu 2 ký tự — chặn email nhập bừa như 'l@gmail.com'.
const MIN_EMAIL_LOCAL_LENGTH = 2;

export default {
  name: 'ReviewFastInvoiceModal',
  components: { TableColumnFilter, VatNameFilter },
  props: {
    // 'create' → Trang Hóa đơn (Tạo hóa đơn đỏ) · 'publish' → Trang Hóa đơn đỏ (Phát hành FAST)
    mode: {
      type: String,
      default: 'publish',
      validator: (value) => ['create', 'publish'].includes(value),
    },
  },
  data() {
    return {
      VAT_TYPE,
      RESULT_STATUS,
      RETURN_STATE,
      CHECK_TAG_META,
      // Loại hóa đơn nguồn (1=bán lẻ, 2=bán buôn) — quyết định mặc định B2B/B2C khi TẠO hóa đơn đỏ.
      billTypeContext: null,
      // billNumber của các HĐ đang mở rộng xem chi tiết sản phẩm.
      expandedRows: [],
      // Kéo tay cầm ô email xuống: gán cùng giá trị cho các dòng bên dưới.
      emailFill: null,
      // Chế độ chỉnh sửa cho luồng PHÁT HÀNH (publish): false = chỉ xem, true = cho sửa KH trước khi đẩy.
      editMode: false,
      editModeOptions: [
        { value: false, text: 'Xem' },
        { value: true, text: 'Chỉnh sửa' },
      ],
      rows: [],
      // Bộ lọc kiểu Excel theo từng cột: key cột → mảng giá trị đang chọn (rỗng = không lọc).
      filters: {
        maHD: [],
        vatType: [],
        tenKH: [],
        mst: [],
        sdt: [],
        diaChi: [],
        email: [],
        kiemTra: [],
        trangThai: [],
      },
      // Bộ lọc "Tên xuất VAT": mảng tên VAT (đã chuẩn hóa) đang chọn; rỗng = không lọc.
      vatNameFilter: [],
      loading: false,
      submitting: false,
      isSubmitted: false,
      fromPartner: 'FAST',
      fromPartnerOptions: [
        { value: 'MIFI', text: 'MIFI' },
        { value: 'FAST', text: 'FAST' },
      ],
      vatTypeOptions: [
        { value: VAT_TYPE.COMPANY, text: 'B2B' },
        { value: VAT_TYPE.INDIVIDUAL, text: 'B2C' },
      ],
    };
  },
  computed: {
    isCreateMode() {
      return this.mode === 'create';
    },
    // Ô khách hàng hiện dạng ô nhập: luôn ở bước Tạo; bước Phát hành chỉ khi bật "Chỉnh sửa".
    customerEditable() {
      return this.isCreateMode || this.editMode;
    },
    modalTitle() {
      return this.isCreateMode
        ? 'Kiểm tra & Tạo hóa đơn đỏ'
        : 'Kiểm tra & Phát hành hóa đơn FAST';
    },
    confirmLabel() {
      return this.isCreateMode ? 'Tạo hóa đơn đỏ' : 'Phát hành';
    },
    includeColumnHint() {
      return this.isCreateMode
        ? 'Chọn = xuất dòng này lên hóa đơn VAT; bỏ chọn = không xuất dòng đó. Dòng 0đ mặc định được chọn — kế toán quyết ở bước Phát hành'
        : 'Chỉ dòng 0đ được bỏ chọn: bỏ chọn = loại dòng khỏi hóa đơn đỏ khi phát hành (không đẩy lên FAST)';
    },
    successRowLabel() {
      return this.isCreateMode ? 'Đã tạo hóa đơn đỏ' : 'Đã phát hành';
    },
    defaultFailMessage() {
      return this.isCreateMode
        ? 'Tạo hóa đơn đỏ thất bại'
        : 'Phát hành hóa đơn thất bại';
    },
    validRows() {
      // "Hợp lệ" = server KHÔNG fatal VÀ KHÔNG lỗi email client-side (B2B bắt buộc email đúng định dạng).
      return this.rows.filter(
        (row) => !row.isFatal && !this.emailErrorFor(row),
      );
    },
    invalidRows() {
      // "Không hợp lệ" gồm CẢ lỗi server (isFatal) LẪN lỗi email client-side — khớp cột trạng thái,
      // cảnh báo email, và điều kiện chặn submit (canSubmit / emailInvalidCount).
      return this.rows.filter(
        (row) => row.isFatal || this.emailErrorFor(row),
      );
    },
    canSubmit() {
      return (
        !this.submitting &&
        !this.loading &&
        this.pendingRows.length > 0 &&
        this.emailInvalidCount === 0
      );
    },
    // Số hóa đơn (trong các dòng còn xử lý) đang có email chưa hợp lệ → chặn submit + cảnh báo.
    emailInvalidCount() {
      return this.pendingRows.filter((row) => this.emailErrorFor(row)).length;
    },
    successCount() {
      return this.rows.filter(
        (row) => row.resultStatus === RESULT_STATUS.SUCCESS,
      ).length;
    },
    failedCount() {
      return this.rows.filter(
        (row) => row.resultStatus === RESULT_STATUS.FAILED,
      ).length;
    },
    // Dòng còn cần xử lý: hợp lệ và CHƯA thành công (dùng cho submit + thử lại). Đang lọc → CHỈ các hóa
    // đơn đang hiển thị (lọc "Không vấn đề" rồi bấm xử lý không được kéo theo hóa đơn bị ẩn).
    pendingRows() {
      const source = this.isFiltering ? this.filteredRows : this.rows;
      return source.filter(
        (row) => !row.isFatal && row.resultStatus !== RESULT_STATUS.SUCCESS,
      );
    },
    submitCountLabel() {
      const count = this.pendingRows.length;
      return this.isFiltering ? `${count} hóa đơn đang lọc` : String(count);
    },
    // Đề xuất chưa áp, gom theo field cho nút "Áp dụng" hàng loạt. Chỉ ở bước Tạo (nơi hiện đề xuất);
    // cùng phạm vi với nút xử lý: đang lọc → chỉ hóa đơn đang hiển thị.
    suggestionGroups() {
      if (!this.isCreateMode) return [];
      const source = this.isFiltering ? this.filteredRows : this.rows;
      return SUGGESTION_FIELDS.map(({ field, label }) => ({
        field,
        label,
        rows: source.filter(
          (row) => this.isEditable(row) && this.suggestionFor(row, field),
        ),
      })).filter((group) => group.rows.length);
    },
    // Dòng 0đ cho bỏ tích HÀNG LOẠT ở bước phát hành (t101269). Cùng phạm vi nút Phát hành (đang lọc → chỉ
    // hóa đơn đang hiển thị) + cùng điều kiện ô tích từng dòng (`isIncludeLocked`); có lọc "Tên xuất VAT" →
    // chỉ dòng mang tên đã chọn (1 mã hết tồn bản thuế → loại mã đó ở mọi hóa đơn, giữ dòng 0đ khác).
    zeroBulk() {
      const empty = { details: [], invoiceCount: 0, includedCount: 0, excludedCount: 0 };
      if (this.isCreateMode) return empty;
      const source = this.isFiltering ? this.filteredRows : this.rows;
      const selectedVatKeys = new Set(this.vatNameFilter);
      let invoiceCount = 0;
      const details = [];
      source.forEach((row) => {
        const matched = (row.details || []).filter(
          (d) =>
            !this.isIncludeLocked(row, d) &&
            (!selectedVatKeys.size || selectedVatKeys.has(this.detailVatKey(d))),
        );
        if (!matched.length) return;
        invoiceCount += 1;
        details.push(...matched);
      });
      const includedCount = details.filter((d) => d.include).length;
      return {
        details,
        invoiceCount,
        includedCount,
        excludedCount: details.length - includedCount,
      };
    },
    hasResults() {
      return this.rows.some((row) => row.resultStatus);
    },
    // Số cột của bảng chính (dùng cho colspan dòng chi tiết sản phẩm).
    detailColspan() {
      return this.filterColumns.length;
    },
    // Badge bán lẻ / bán buôn cạnh số HĐ (theo trang nguồn khi TẠO). Publish không có ngữ cảnh này → ẩn.
    billTypeBadge() {
      if (this.billTypeContext === BILL_TYPE.WHOLESALE_INVOICE) {
        return { text: 'Bán buôn', class: 'label-light-warning' };
      }
      if (this.billTypeContext === BILL_TYPE.RETAIL_INVOICE) {
        return { text: 'Bán lẻ', class: 'label-light-info' };
      }
      return null;
    },
    // Danh sách cột có nút lọc — 2 bước dùng chung 5 cột khách hàng (Tên · MST · SĐT · Địa chỉ · Email).
    filterColumns() {
      const tailColumns = [
        { key: 'kiemTra', label: 'Kiểm tra', align: 'start', minWidth: '140px' },
        { key: 'trangThai', label: 'Trạng thái', align: 'start', minWidth: '168px' },
      ];
      return [
        { key: 'maHD', label: 'Mã HĐ', align: 'center', minWidth: '96px' },
        { key: 'vatType', label: 'B2B/B2C', align: 'center', minWidth: '108px' },
        { key: 'tenKH', label: 'Tên khách hàng', align: 'start', minWidth: '200px' },
        { key: 'mst', label: 'MST', align: 'start', minWidth: '190px' },
        { key: 'sdt', label: 'SĐT', align: 'start', minWidth: '120px' },
        { key: 'diaChi', label: 'Địa chỉ', align: 'start', minWidth: '200px' },
        { key: 'email', label: 'Email', align: 'start', minWidth: '190px' },
        ...tailColumns,
      ];
    },
    // Giá trị hiển thị DUY NHẤT mỗi cột (suy từ TOÀN BỘ rows) để đổ vào dropdown lọc.
    optionsByColumn() {
      const result = {};
      this.filterColumns.forEach((col) => {
        const seen = new Set();
        this.rows.forEach((row) =>
          this.columnValues(row, col.key).forEach((value) => seen.add(value)),
        );
        result[col.key] = Array.from(seen).sort((a, b) =>
          String(a).localeCompare(String(b), 'vi', { numeric: true }),
        );
      });
      return result;
    },
    // Tập hợp tên xuất VAT (đã chuẩn hóa) DUY NHẤT trên TOÀN BỘ dòng — đổ vào bộ lọc "Tên xuất VAT".
    // Mỗi tên kèm cờ IMEI (bất kỳ dòng nào của tên đó là IMEI) + tổng SỐ LƯỢNG (×N) toàn bộ hóa đơn.
    vatNameOptions() {
      const map = new Map();
      this.rows.forEach((row) => {
        (row.details || []).forEach((d) => {
          // Khóa theo detailVatKey (F7): SP thiếu tên VAT KHÔNG gộp chung 1 nhóm (khóa theo productId),
          // nhưng SP KHÁC NHAU cùng tên VAT thật thì vẫn gộp (đúng ý "lọc theo tên").
          const key = this.detailVatKey(d);
          const real = this.realVatName(d);
          // Nhãn SP thiếu tên kèm mã SP để phân biệt trong list lọc (R2-3).
          const name = real || `(Thiếu tên VAT) · SP #${d.productId || d.billItemId || '?'}`;
          const qty = Number(d.quantity) || 0;
          const entry = map.get(key) || { key, name, isImei: false, qty: 0 };
          entry.qty += qty;
          if (d.isImei) entry.isImei = true;
          map.set(key, entry);
        });
      });
      return Array.from(map.values()).sort((a, b) =>
        String(a.name).localeCompare(String(b.name), 'vi', { numeric: true }),
      );
    },
    // Dòng hiển thị SAU khi áp bộ lọc cột + bộ lọc tên xuất VAT. KHÔNG đụng `rows` gốc.
    filteredRows() {
      return this.rows.filter(
        (row) =>
          this.filterColumns.every((col) => {
            const selected = this.filters[col.key];
            if (!selected || !selected.length) return true;
            return this.columnValues(row, col.key).some((value) =>
              selected.includes(value),
            );
          }) && this.rowMatchesVatFilter(row),
      );
    },
    isFiltering() {
      return (
        this.vatNameFilter.length > 0 ||
        this.filterColumns.some((col) => {
          const selected = this.filters[col.key];
          return selected && selected.length > 0;
        })
      );
    },
  },
  beforeDestroy() {
    this.stopEmailFillListeners();
    this.clearEmailFillArm();
  },
  methods: {
    // Trích giá trị HIỂN THỊ của 1 ô theo cột (khớp đúng nội dung đang thấy trên bảng).
    // Dùng CHUNG cho việc dựng option lọc và so khớp khi lọc → option ≡ giá trị hàng (single-source).
    columnValue(row, key) {
      const form = row.form || {};
      switch (key) {
        case 'maHD':
          return String(row.billNumber == null ? '' : row.billNumber);
        case 'vatType':
          return row.billVatType === VAT_TYPE.COMPANY ? 'B2B' : 'B2C';
        case 'tenKH':
          return form.billVatName || '';
        case 'mst':
          return form.taxCode || '';
        case 'sdt':
          return form.phone || '';
        case 'diaChi':
          return form.billVatAddress || '';
        case 'email':
          return form.email || '';
        case 'kiemTra':
          return this.columnValues(row, key).join(', ');
        case 'trangThai':
          return this.rowStatusLabel(row);
        default:
          return '';
      }
    },
    // Giá trị lọc của 1 ô — mảng để cột ĐA NHÃN ("Kiểm tra") khớp khi BẤT KỲ nhãn nào được chọn.
    // Dùng CHUNG cho dựng option và so khớp lọc (option ≡ giá trị hàng).
    columnValues(row, key) {
      if (key === 'kiemTra') {
        const tags = this.rowCheckTags(row);
        return tags.length ? tags : [CHECK_TAG.NONE];
      }
      return [this.columnValue(row, key)];
    },
    remainingQuantity(product) {
      return Math.max((product.soldQuantity || 0) - (product.returnedQuantity || 0), 0);
    },
    // Nhãn kiểm tra trước khi xuất của 1 hóa đơn (t101269) — suy từ cờ BE trả, FE không tự tính luật.
    rowCheckTags(row) {
      const details = row.details || [];
      const tags = [];
      if (row.returnStatus === RETURN_STATE.FULL) tags.push(CHECK_TAG.RETURN_FULL);
      if (row.returnStatus === RETURN_STATE.PARTIAL) tags.push(CHECK_TAG.RETURN_PARTIAL);
      if (details.some((d) => d.isProductInactive)) tags.push(CHECK_TAG.INACTIVE);
      if (details.some((d) => d.needsTaxStockCheck)) tags.push(CHECK_TAG.ZERO);
      if (details.some((d) => d.vatParentNotFound)) tags.push(CHECK_TAG.PARENT_MISSING);
      return tags;
    },
    // Nhãn trạng thái gộp cho việc lọc (khớp cột Trạng thái): kết quả đẩy > lỗi > hợp lệ.
    rowStatusLabel(row) {
      if (row.resultStatus === RESULT_STATUS.SUCCESS) return 'Thành công';
      if (row.resultStatus === RESULT_STATUS.FAILED) return 'Thất bại';
      if ((row.errors && row.errors.length) || this.emailErrorFor(row)) {
        return 'Không hợp lệ';
      }
      return 'Hợp lệ';
    },
    clearAllFilters() {
      this.filterColumns.forEach((col) => {
        this.filters[col.key] = [];
      });
      this.vatNameFilter = [];
    },
    // Tên xuất VAT THỰC của 1 dòng (rỗng nếu SP thiếu hoàn toàn tên) — BE trả vatDisplayName đã chuẩn hóa.
    realVatName(detail) {
      return detail.vatDisplayName || detail.productNameVat || detail.productName || '';
    },
    // Tên hiển thị (có placeholder khi thiếu).
    detailVatName(detail) {
      return this.realVatName(detail) || '(Thiếu tên VAT)';
    },
    // Khóa gộp/lọc: tên thật nếu có; thiếu thì khóa theo productId/billItemId để KHÔNG gộp nhầm (F7).
    detailVatKey(detail) {
      const name = this.realVatName(detail);
      if (name) return name;
      return `__missing__${detail.productId || detail.billItemId || ''}`;
    },
    // Hóa đơn khớp bộ lọc "Tên xuất VAT" khi có ÍT NHẤT 1 dòng mang KHÓA đang được chọn.
    rowMatchesVatFilter(row) {
      if (!this.vatNameFilter.length) return true;
      const selected = new Set(this.vatNameFilter);
      return (row.details || []).some((d) => selected.has(this.detailVatKey(d)));
    },
    isExpanded(billNumber) {
      return this.expandedRows.includes(billNumber);
    },
    toggleExpand(billNumber) {
      this.expandedRows = this.isExpanded(billNumber)
        ? this.expandedRows.filter((n) => n !== billNumber)
        : [...this.expandedRows, billNumber];
    },
    // Thuế suất 1 dòng SP: -1 = KCT (không chịu thuế), null = chưa có, còn lại 'X%'.
    detailVatLabel(detail) {
      const vat = Number(detail.vatAmount);
      if (detail.vatAmount == null || Number.isNaN(vat)) return '-';
      return vat === -1 ? 'KCT' : `${vat}%`;
    },
    // Chiết khấu 1 dòng SP = chiết khấu sản phẩm + chiết khấu phân bổ.
    detailDiscount(detail) {
      return (
        (Number(detail.discountAmount) || 0) +
        (Number(detail.allotmentDiscountAmount) || 0)
      );
    },
    // Đề xuất làm sạch (chưa áp) cho 1 field — null nếu không có.
    suggestionFor(row, field) {
      return (row.suggestions || []).find((s) => s.field === field) || null;
    },
    // Cảnh báo (không chặn) cho 1 field.
    warningsFor(row, field) {
      return (row.warnings || []).filter((w) => w.field === field);
    },
    // Chỉ enforce validate email khi ô email đang hiển thị & cho sửa (tạo mới, hoặc phát hành + chế độ Chỉnh sửa).
    isEmailEnforced(row) {
      return this.isEditable(row) && this.customerEditable;
    },
    // Kiểm tra chuỗi email (có thể nhiều email cách nhau bởi ';') theo loại HĐ.
    // B2B (COMPANY): bắt buộc nhập + mỗi email đúng định dạng.
    // B2C (INDIVIDUAL): không bắt buộc, nhưng nếu nhập thì mỗi email phải đúng định dạng.
    // Trả về '' nếu hợp lệ, hoặc message lỗi để hiển thị/chặn submit.
    computeEmailError(rawEmail, billVatType) {
      const email = (rawEmail || '').trim();
      const isCompany = billVatType === VAT_TYPE.COMPANY;
      if (!email) {
        return isCompany ? 'B2B bắt buộc nhập email.' : '';
      }
      if (email.length > MAX_EMAIL_LENGTH) {
        return `Email không được vượt quá ${MAX_EMAIL_LENGTH} ký tự.`;
      }
      const emails = email
        .split(';')
        .map((item) => item.trim())
        .filter((item) => item);
      if (!emails.length) {
        return isCompany ? 'B2B bắt buộc nhập email.' : '';
      }
      // Mỗi email phải đúng định dạng.
      const invalid = emails.find((item) => !isValidEmail(item));
      if (invalid) return `Email không hợp lệ: ${invalid}`;
      // Thêm luật: phần trước dấu '@' tối thiểu 2 ký tự (chặn nhập bừa như 'l@gmail.com').
      const shortLocal = emails.find(
        (item) => item.split('@')[0].length < MIN_EMAIL_LOCAL_LENGTH,
      );
      if (shortLocal) {
        return `Email không hợp lệ: ${shortLocal} — phần trước dấu "@" phải có ít nhất ${MIN_EMAIL_LOCAL_LENGTH} ký tự.`;
      }
      return '';
    },
    // Lỗi email của 1 dòng (chỉ tính khi đang enforce) — dùng cho tô đỏ dòng, hiển thị & chặn submit.
    isEmailFillTarget(rowIndex) {
      return (
        this.emailFill &&
        rowIndex > this.emailFill.start &&
        rowIndex <= this.emailFill.end
      );
    },
    armEmailFill(event, rowIndex) {
      if (event.button !== 0) return;
      const row = this.filteredRows[rowIndex];
      // Phát hành + "Xem": ô email là chữ chỉ đọc → không cho kéo-gán email.
      if (!row || !this.isEditable(row) || !this.customerEditable) return;
      this.emailFillArm = {
        rowIndex,
        x: event.clientX,
        y: event.clientY,
        pointerId: event.pointerId,
      };
      window.addEventListener('pointermove', this.onEmailFillArmMove);
      window.addEventListener('pointerup', this.clearEmailFillArm);
      window.addEventListener('pointercancel', this.clearEmailFillArm);
    },
    onEmailFillArmMove(event) {
      const arm = this.emailFillArm;
      if (!arm || event.pointerId !== arm.pointerId) return;
      const dy = event.clientY - arm.y;
      if (dy < 6) return;
      const rowIndex = arm.rowIndex;
      this.clearEmailFillArm();
      const selection = window.getSelection && window.getSelection();
      if (selection && selection.removeAllRanges) selection.removeAllRanges();
      this.startEmailFill(rowIndex);
      this.onEmailFillMove(event);
    },
    clearEmailFillArm() {
      this.emailFillArm = null;
      window.removeEventListener('pointermove', this.onEmailFillArmMove);
      window.removeEventListener('pointerup', this.clearEmailFillArm);
      window.removeEventListener('pointercancel', this.clearEmailFillArm);
    },
    startEmailFill(rowIndex) {
      const row = this.filteredRows[rowIndex];
      if (!row || !this.isEditable(row)) return;
      this.emailFill = {
        start: rowIndex,
        end: rowIndex,
        value: row.form.email || '',
      };
      document.body.classList.add('rf-email-filling');
      window.addEventListener('pointermove', this.onEmailFillMove);
      window.addEventListener('pointerup', this.onEmailFillUp);
      window.addEventListener('pointercancel', this.onEmailFillUp);
    },
    onEmailFillMove(event) {
      if (!this.emailFill) return;
      const hit = document.elementFromPoint(event.clientX, event.clientY);
      const rowEl = hit && hit.closest ? hit.closest('tr[data-email-index]') : null;
      if (!rowEl) return;
      const index = Number(rowEl.dataset.emailIndex);
      if (Number.isNaN(index) || index < this.emailFill.start) return;
      this.emailFill.end = index;
    },
    onEmailFillUp() {
      const fill = this.emailFill;
      this.stopEmailFillListeners();
      document.body.classList.remove('rf-email-filling');
      this.emailFill = null;
      if (!fill || fill.end <= fill.start) return;
      const rows = this.filteredRows;
      for (let i = fill.start + 1; i <= fill.end; i += 1) {
        const target = rows[i];
        if (target && this.isEditable(target)) {
          target.form.email = fill.value;
        }
      }
    },
    stopEmailFillListeners() {
      document.body.classList.remove('rf-email-filling');
      window.removeEventListener('pointermove', this.onEmailFillMove);
      window.removeEventListener('pointerup', this.onEmailFillUp);
      window.removeEventListener('pointercancel', this.onEmailFillUp);
    },
    emailErrorFor(row) {
      if (!this.isEmailEnforced(row)) return '';
      return this.computeEmailError(row.form.email, row.billVatType);
    },
    // Ghi giá trị đề xuất vào bản làm việc + gỡ đề xuất đó khỏi danh sách.
    applySuggestion(row, suggestion) {
      if (!suggestion) return;
      row.form[suggestion.field] = suggestion.to;
      row.suggestions = row.suggestions.filter((s) => s !== suggestion);
    },
    // Nút "Áp dụng <field> (N)" → áp đề xuất của field đó cho mọi hóa đơn trong nhóm, 1 lượt.
    applySuggestionGroup(group) {
      group.rows.forEach((row) =>
        this.applySuggestion(row, this.suggestionFor(row, group.field)),
      );
      makeToastSuccess(
        `Đã áp dụng đề xuất ${group.label} cho ${group.rows.length} hóa đơn.`,
      );
    },
    async bulkExcludeZeroDetails() {
      const { includedCount, invoiceCount } = this.zeroBulk;
      const ok = await this.$bvModal.msgBoxConfirm(
        `Bỏ chọn ${includedCount} dòng 0đ trong ${invoiceCount} hóa đơn? Khi Phát hành, các dòng này bị ` +
          'xóa khỏi hóa đơn đỏ và không đồng bộ sang FAST.',
        {
          title: 'Bỏ chọn dòng 0đ hàng loạt',
          okTitle: 'Bỏ chọn',
          cancelTitle: 'Hủy',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (!ok) return;
      this.setZeroDetailsInclude(false);
    },
    setZeroDetailsInclude(include) {
      const targets = this.zeroBulk.details.filter((d) => d.include !== include);
      targets.forEach((d) => {
        d.include = include;
      });
      makeToastSuccess(
        `Đã ${include ? 'chọn lại' : 'bỏ chọn'} ${targets.length} dòng 0đ.`,
      );
    },
    isEditable(row) {
      // Khóa dòng đã tạo/phát hành THÀNH CÔNG; dòng FATAL (lỗi validate) không sửa ở đây.
      // Dòng chưa xử lý hoặc xử lý THẤT BẠI → vẫn cho sửa để thử lại.
      return row.resultStatus !== RESULT_STATUS.SUCCESS && !row.isFatal;
    },
    // Thành tiền 1 dòng SAU MỌI chiết khấu — BE trả `netAmount` (= số lưu lên hóa đơn đỏ / đẩy FAST), cùng
    // nghĩa ở bước tạo lẫn phát hành. Thiếu (API cũ) → totalAmount.
    lineNetAmount(detail) {
      const net = detail.netAmount != null ? detail.netAmount : detail.totalAmount;
      return Number(net) || 0;
    },
    // Tóm tắt tiền của 1 hóa đơn — CHỈ các dòng đang tích "Xuất" (dòng bỏ tích/bị khóa không lên hóa đơn).
    // Tiền hàng = thành tiền + chiết khấu sản phẩm + chiết khấu tổng đơn phân bổ.
    rowSummary(row) {
      const lines = (row.details || []).filter((d) => d.include);
      const sum = (pick) => lines.reduce((total, d) => total + (Number(pick(d)) || 0), 0);
      const productDiscount = sum((d) => d.discountAmount);
      const allotmentDiscount = sum((d) => d.allotmentDiscountAmount);
      const netAmount = sum((d) => this.lineNetAmount(d));
      return {
        grossAmount: netAmount + productDiscount + allotmentDiscount,
        productDiscount,
        allotmentDiscount,
        netAmount,
        includedCount: lines.length,
      };
    },
    formatMoney(value) {
      return convertPrice(Number(value) || 0);
    },
    // Trả về hàm debounce riêng cho từng dòng (theo billNumber) để tra MST
    // của dòng này không đè lên dòng khác khi người dùng sửa nhiều dòng.
    getTaxDebouncer(row) {
      if (!this.taxDebouncers) this.taxDebouncers = {};
      const key = row.billNumber;
      if (!this.taxDebouncers[key]) {
        this.taxDebouncers[key] = debounce((query) => {
          this.fetchTaxCode(query, row);
        }, TIME_TRIGGER);
      }
      return this.taxDebouncers[key];
    },
    onTaxCodeInput(row, value) {
      // Chỉ tra cứu MST cho dòng B2B đang được phép sửa.
      if (!this.isEditable(row) || row.billVatType !== VAT_TYPE.COMPANY) return;
      const raw = value != null ? String(value).trim() : '';
      if (!raw) return;
      const query = xoa_dau(raw);
      this.getTaxDebouncer(row)(query);
    },
    async fetchTaxCode(query, row) {
      if (!query) return;
      try {
        const response = await ApiService.query('vat-bills/tax-search', {
          params: { taxCode: query, fromPartner: this.fromPartner },
        });
        const data = response.data && response.data.data;
        if (!data) {
          makeToastFaile(`Không tìm thấy thông tin MST cho HĐ ${row.billNumber}`);
          return;
        }
        // Auto-fill đúng dòng đang sửa. Các key đã được khởi tạo trong
        // transformValidateItem nên gán trực tiếp vẫn giữ reactivity.
        if (data.vatName) row.form.billVatName = data.vatName;
        if (data.vatAddress) row.form.billVatAddress = data.vatAddress;
        if (data.taxCode) row.form.taxCode = data.taxCode;
        makeToastSuccess(
          response.data.message ||
            `Đã cập nhật thông tin MST cho HĐ ${row.billNumber}`,
        );
      } catch (error) {
        makeToastFaile(
          error.response?.data?.message ||
            `Không tìm thấy thông tin MST cho HĐ ${row.billNumber}`,
        );
      }
    },
    // Bấm icon cạnh input MST → tra cứu & điền ngay (không đợi debounce).
    lookupTaxCode(row) {
      if (!this.isEditable(row) || row.billVatType !== VAT_TYPE.COMPANY) return;
      const raw =
        row.form.taxCode != null
          ? String(row.form.taxCode).trim()
          : '';
      if (!raw) {
        makeToastFaile('Vui lòng nhập MST để tra cứu.');
        return;
      }
      this.fetchTaxCode(xoa_dau(raw), row);
    },
    open(billIds, billType = null) {
      this.rows = [];
      this.isSubmitted = false;
      this.submitting = false;
      this.taxDebouncers = {};
      this.expandedRows = [];
      this.editMode = false;
      // Lưu TRƯỚC khi validate vì transformValidateItem dùng để set mặc định B2B/B2C.
      this.billTypeContext = billType;
      this.$refs['review-fast-invoice-modal'].show();
      this.validate(billIds);
    },
    hide() {
      this.$refs['review-fast-invoice-modal'].hide();
    },
    onHidden() {
      this.rows = [];
      this.isSubmitted = false;
      this.submitting = false;
      this.taxDebouncers = {};
      this.billTypeContext = null;
      this.expandedRows = [];
      this.editMode = false;
    },
    async validate(billIds) {
      if (!Array.isArray(billIds) || !billIds.length) {
        makeToastFaile('Vui lòng chọn ít nhất một đơn hàng để kiểm tra');
        return;
      }
      this.loading = true;
      try {
        const res = await ApiService.post('vat-bills/fast-invoices/validate', {
          billIds,
          mode: this.mode,
        });
        const { status, message, data } = res.data;
        if (status !== 1) {
          makeToastFaile(message || 'Kiểm tra hóa đơn thất bại');
          return;
        }
        this.rows = (data || []).map((item) => this.transformValidateItem(item));
        // Mặc định MỞ SẴN phần sản phẩm của mọi hóa đơn (thay vì ẩn) để user thấy ngay chi tiết.
        this.expandedRows = this.rows
          .filter((row) => row.details && row.details.length)
          .map((row) => row.billNumber);
      } catch (error) {
        makeToastFaile(
          error.response?.data?.message ||
            'Có lỗi xảy ra khi kiểm tra hóa đơn. Vui lòng thử lại.',
        );
      } finally {
        this.loading = false;
      }
    },
    // Dòng KHÔNG đủ điều kiện xuất VAT (thiếu tên VAT / thuế suất VAT) — bám luật của luồng tạo đơn lẻ
    // (BillVatDetailModel.checkValidate). BE trả cờ hasVatName/hasVatRate; undefined = API cũ → coi là hợp lệ.
    // t101269: + `includeBlockReasons` (mã hết hiệu lực / đã trả hàng) — CÙNG predicate BE dùng chặn create-batch.
    isDetailInvalid(detail) {
      return (
        detail.hasVatName === false ||
        detail.hasVatRate === false ||
        (detail.includeBlockReasons || []).length > 0
      );
    },
    detailInvalidReason(detail) {
      const reasons = [];
      if (detail.hasVatName === false) reasons.push('Yêu cầu tên VAT');
      if (detail.hasVatRate === false) reasons.push('Yêu cầu thuế suất VAT');
      reasons.push(...(detail.includeBlockReasons || []));
      return `${reasons.join(' · ')} — không thể xuất dòng này`;
    },
    // Bước tạo: chỉ khóa dòng không xuất được (như trước t101269), dòng 0đ vẫn tích/bỏ tích được. Bước phát hành
    // CHỈ cho bỏ tích dòng 0đ (cờ BE `needsTaxStockCheck` — cùng cờ issue-batch dùng kiểm `excludedDetailIds`).
    isIncludeLocked(row, detail) {
      if (!this.isEditable(row)) return true;
      if (!this.isCreateMode) return !detail.needsTaxStockCheck;
      return this.isDetailInvalid(detail);
    },
    detailIncludeTooltip(detail) {
      if (this.isCreateMode) {
        if (this.isDetailInvalid(detail)) return this.detailInvalidReason(detail);
        if (detail.isZeroValue) {
          return detail.include
            ? 'Dòng 0đ mặc định được đưa vào hóa đơn đỏ — kế toán quyết đồng bộ FAST ở bước Phát hành'
            : 'Dòng 0đ KHÔNG xuất lên hóa đơn VAT — bước Phát hành không thêm lại được, chọn để đưa vào';
        }
        return detail.include
          ? 'Dòng này SẼ được xuất lên hóa đơn VAT — bỏ chọn để loại khỏi hóa đơn'
          : 'Dòng này KHÔNG xuất lên hóa đơn VAT — chọn để thêm vào hóa đơn';
      }
      if (!detail.needsTaxStockCheck) {
        return 'Dòng có trên hóa đơn đỏ — luôn được đẩy lên FAST';
      }
      return detail.include
        ? 'Bỏ chọn sẽ không đồng bộ sản phẩm này sang FAST'
        : 'Sản phẩm này sẽ không đồng bộ sang FAST — chọn lại để đồng bộ';
    },
    zeroValueHint(detail) {
      if (this.isCreateMode) {
        return 'Dòng 0đ — kế toán kiểm tồn kho bản thuế và quyết đẩy hay loại ở bước Phát hành';
      }
      return detail.needsTaxStockCheck
        ? 'Dòng 0đ — kiểm tra tồn kho bản thuế trên FAST; không còn tồn thì bỏ chọn để loại khỏi hóa đơn đỏ'
        : '';
    },
    // Số dòng 0đ kế toán đã bỏ tích (sẽ bị loại khỏi hóa đơn đỏ khi phát hành).
    excludedZeroCount(row) {
      return (row.details || []).filter((d) => d.needsTaxStockCheck && !d.include)
        .length;
    },
    transformValidateItem(item) {
      const original = item.originalData || {};
      let billVatType = original.billVatType || VAT_TYPE.COMPANY;
      // Khi TẠO hóa đơn đỏ: bán buôn (wholesale) mặc định B2B; bán lẻ giữ mặc định theo BE
      // (taxCode ? B2B : B2C). Không áp cho luồng phát hành (publish) vì loại đã cố định trên HĐĐ.
      if (
        this.isCreateMode &&
        this.billTypeContext === BILL_TYPE.WHOLESALE_INVOICE
      ) {
        billVatType = VAT_TYPE.COMPANY;
      }
      // Tạo: mọi dòng hợp lệ mặc định XUẤT, kể cả dòng 0đ (t101269 — kế toán quyết ở bước phát hành; cửa hàng
      // vẫn bỏ tích được); dòng thiếu tên VAT / thuế suất / bị chặn KHÔNG bao giờ được xuất.
      // Phát hành: mọi dòng đã có trên hóa đơn đỏ mặc định ĐẨY; kế toán bỏ tích dòng 0đ để loại.
      const details = (Array.isArray(item.details) ? item.details : []).map(
        (d) => ({
          ...d,
          include: this.isCreateMode ? !this.isDetailInvalid(d) : true,
        }),
      );
      return {
        billId: item.billId,
        billNumber: item.billNumber,
        billVatType,
        // `form` = bản làm việc, khởi tạo từ dữ liệu GỐC (KHÔNG auto-clean). User tự sửa / bấm áp dụng đề xuất.
        form: {
          billVatName: original.billVatName || '',
          taxCode: original.taxCode || '',
          billVatAddress: original.billVatAddress || '',
          email: original.email || '',
          phone: original.phone || '',
        },
        suggestions: Array.isArray(item.suggestions) ? item.suggestions : [],
        warnings: Array.isArray(item.warnings) ? item.warnings : [],
        details,
        errors: Array.isArray(item.errors) ? item.errors : [],
        isFatal: Boolean(item.isFatal),
        returnStatus: item.returnStatus || RETURN_STATE.NONE,
        // Bảng đối chiếu SL bán ↔ SL trả theo SP (null = bill không có phiếu trả).
        returnSummary: item.returnSummary || null,
        resultStatus: null,
        billVatId: null,
        fastInvoiceNo: null,
        fastSyncStatusId: null,
        error: null,
      };
    },
    async onSubmitBatch() {
      if (!this.canSubmit) return;
      this.submitting = true;
      const invoices = this.pendingRows.map((row) => ({
        billId: row.billId,
        billNumber: row.billNumber,
        sanitizedData: {
          ...row.form,
          billVatType: row.billVatType,
        },
        // Chỉ luồng PHÁT HÀNH: khi user bật "Chỉnh sửa" → BE cập nhật lại thông tin KH trước khi đẩy FAST.
        edited: !this.isCreateMode && this.editMode,
        // Dòng user chọn xuất (theo billItemId) — BE tạo HĐ đúng các dòng này (gồm cả dòng 0đ).
        includedBillItemIds: row.details
          .filter((d) => d.include)
          .map((d) => d.billItemId)
          .filter((id) => id != null),
        // Phát hành: dòng 0đ kế toán bỏ tích (id BillVatDetail) — BE loại khỏi hóa đơn đỏ trước khi đẩy.
        excludedDetailIds: this.isCreateMode
          ? []
          : row.details
              .filter((d) => d.needsTaxStockCheck && !d.include)
              .map((d) => d.id)
              .filter((id) => id != null),
      }));
      try {
        let res;
        if (this.isCreateMode) {
          res = await ApiService.post('vat-bills/fast-invoices/create-batch', {
            invoices,
          });
        } else {
          const idempotenceKey = uuidv4();
          res = await ApiService.post(
            'vat-bills/fast-invoices/issue-batch',
            { invoices },
            idempotenceKey,
          );
        }
        const { status, message, data } = res.data;
        if (status !== 1) {
          makeToastFaile(message || this.defaultFailMessage);
          return;
        }
        const results = data || [];
        this.applyResult(results);
        this.isSubmitted = true;
        this.notifyBatchResult(results);
        this.$emit('submitted');

        // Toàn bộ dòng THÀNH CÔNG → đóng modal luôn. Còn bất kỳ dòng lỗi/không hợp lệ → đẩy lên đầu
        // để user thấy ngay (không phải cuộn tìm).
        const allSuccess =
          this.rows.length > 0 &&
          this.rows.every((row) => row.resultStatus === RESULT_STATUS.SUCCESS);
        if (allSuccess) {
          this.hide();
        } else {
          this.reorderRowsByResult();
        }
      } catch (error) {
        makeToastFaile(
          error.response?.data?.message ||
            'Có lỗi xảy ra khi xử lý. Vui lòng thử lại.',
        );
      } finally {
        this.submitting = false;
      }
    },
    // Đẩy các dòng LỖI lên đầu sau khi phát hành: lỗi phát hành (FAILED) → không hợp lệ (fatal/email) →
    // còn lại (thành công/hợp lệ). Stable (giữ thứ tự tương đối trong cùng nhóm).
    reorderRowsByResult() {
      const rank = (row) => {
        if (row.resultStatus === RESULT_STATUS.FAILED) return 0;
        if (row.isFatal || this.emailErrorFor(row)) return 1;
        return 2;
      };
      this.rows = this.rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
        .map((entry) => entry.row);
    },
    applyResult(results) {
      const resultMap = {};
      results.forEach((result) => {
        resultMap[result.billNumber] = result;
      });
      this.rows.forEach((row) => {
        const result = resultMap[row.billNumber];
        if (!result) return;
        row.resultStatus = result.status;
        row.billVatId = result.billVatId || null;
        row.fastInvoiceNo = result.fastInvoiceNo || null;
        row.fastSyncStatusId = result.fastSyncStatusId || null;
        row.error = result.error || null;
      });
    },
    // Thông báo tóm tắt theo KẾT QUẢ thực tế: tất cả OK / một phần lỗi / lỗi toàn bộ.
    notifyBatchResult(results) {
      const total = results.length;
      if (!total) return;
      const success = results.filter((r) => r.status === 'SUCCESS').length;
      const failed = total - success;
      const action = this.confirmLabel; // 'Tạo hóa đơn đỏ' | 'Phát hành'

      if (failed === 0) {
        makeToastSuccess(`${action} thành công ${success}/${total} hóa đơn.`);
      } else if (success === 0) {
        makeToastFaile(
          `${action} thất bại toàn bộ ${failed}/${total} hóa đơn. Vui lòng kiểm tra các dòng lỗi.`,
        );
      } else {
        makeToastWarning(
          `${action}: thành công ${success} hóa đơn, ${failed} hóa đơn thất bại. Vui lòng kiểm tra các dòng lỗi.`,
        );
      }
    },
    // Sau khi tạo hóa đơn đỏ thành công → điều hướng sang trang danh sách hóa đơn đỏ.
    goToVatInvoiceList() {
      this.hide();
      if (this.$route && this.$route.name === 'vat-invoice-v2') return;
      this.$router.push({ name: 'vat-invoice-v2' }).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* Cho phép bảng tràn ngang + cuộn khi tổng min-width các cột lớn hơn bề rộng modal. */
.table-responsive {
  overflow-x: auto;
}

.review-fast-table {
  width: 100%;
}

.review-fast-table thead th.rf-th {
  padding: 0;
  background-color: #f2f2f2;
  border-color: #d0d0d0;
  border-bottom: 2px solid #b4b4b4;
  color: #333333;
  font-size: 12px;
  font-weight: 600;
  vertical-align: middle;
  white-space: nowrap;
}

.rf-th-inner {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 4px 4px 4px 8px;
}

.rf-th-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.rf-th-label.is-start {
  text-align: left;
}

.rf-th-label.is-center {
  text-align: center;
}

.rf-th-label.is-end {
  text-align: right;
}

.rf-th-filter {
  flex: 0 0 auto;
}

.rf-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
  min-height: 32px;
  margin-bottom: 8px;
  padding: 4px 10px;
  background: #e1f0ff;
  border: 1px solid #3699ff;
  border-radius: 0.42rem;
  color: #3699ff;
  font-size: 12px;
  line-height: 1.4;
}

.rf-filter-note {
  flex: 1 1 100%;
  color: #3f4254;
}

.rf-return-table {
  font-size: 12px;
  max-width: 720px;
}

.rf-return-table .rf-return-hit {
  background: #fff4de;
}

.rf-return-table .rf-return-qty {
  width: 72px;
  white-space: nowrap;
}

.rf-filter-clear {
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  color: #3699ff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
  padding: 4px 8px;
  min-height: 24px;
}

.rf-filter-clear:hover,
.rf-filter-clear:focus {
  text-decoration: underline;
}

.review-fast-table td {
  vertical-align: middle;
}

/* Dòng có lỗi (isFatal) — tô nền đỏ nhạt từng ô để nổi rõ, không chỉ hiện message. */
.review-fast-table tr.review-fast-error-row > td {
  background-color: #ffd5d9 !important;
}

.review-fast-table tr.rf-email-fill-target > td {
  box-shadow: inset 0 0 0 1px #3699ff;
}

.rf-email-cell.is-fillable,
.rf-email-cell.is-fillable .form-control {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Cpath d='M9 1h2v18H9zM1 9h18v2H1z' fill='%23000'/%3E%3C/svg%3E")
      10 10,
    cell;
}

.review-fast-expand-btn.btn {
  color: #3699ff;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
}

/* Nhóm theo hóa đơn: vạch đậm giữa 2 hóa đơn liền nhau (thay viền 2px mặc định của tbody + tbody). */
.review-fast-table > tbody.rf-bill-group + tbody.rf-bill-group {
  border-top: 0;
}

.review-fast-table > tbody.rf-bill-group + tbody.rf-bill-group > tr:first-child > td {
  border-top: 3px solid #a1a5b7;
}

/* Dòng hóa đơn nền xanh nhạt để tách khỏi dòng sản phẩm; dòng lỗi vẫn đỏ (!important ở trên). */
.review-fast-table tr.rf-bill-row > td {
  background-color: #eef5ff;
}

/* Hóa đơn đang mở: thanh xanh bên trái nối dòng hóa đơn với bảng sản phẩm của nó. */
.review-fast-table tbody.is-expanded > tr > td:first-child {
  border-left: 4px solid #3699ff;
}

/* Dòng chi tiết sản phẩm mở rộng dưới mỗi hóa đơn. */
.review-fast-detail-row > td {
  background-color: #fafbfc;
}

.rf-detail-panel {
  padding: 10px 16px 16px 28px;
}

.rf-detail-title {
  margin-bottom: 8px;
  color: #3f4254;
  font-size: 12px;
  font-weight: 600;
}

.rf-detail-title .fa-level-up-alt {
  color: #3699ff;
  font-size: 11px;
}

.rf-detail-bill {
  color: #3699ff;
}

.rf-detail-count {
  color: #7e8299;
  font-weight: 400;
}

.rf-detail-excluded {
  color: #f64e60;
  font-weight: 600;
}

/* Tóm tắt tiền dưới bảng sản phẩm, căn phải (thay 3 cột Thành tiền / Chiết khấu / VAT ở bảng hóa đơn). */
.rf-detail-summary {
  width: 360px;
  max-width: 100%;
  margin: 8px 0 0 auto;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid #ebedf3;
  border-radius: 4px;
  font-size: 12px;
}

.rf-sum-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 2px 0;
  color: #3f4254;
}

.rf-sum-row > span:last-child {
  text-align: right;
  white-space: nowrap;
}

.rf-sum-total {
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid #ebedf3;
  font-weight: 700;
  color: #181c32;
}

.rf-sum-note {
  margin-top: 4px;
  color: #b5b5c3;
  font-size: 11px;
  text-align: right;
}

.review-fast-detail-table {
  background-color: #fff;
  font-size: 0.8rem;
}

.review-fast-detail-table thead th {
  padding: 6px 8px;
  background-color: #f7f8fa;
  color: #7e8299;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

/* Cột tên sản phẩm: giới hạn bề rộng và cho xuống dòng khi tên dài,
   để người dùng đọc trọn nội dung thay vì phải cuộn ngang. */
.review-fast-detail-table .col-product-name {
  min-width: 140px;
  max-width: 220px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.review-fast-suggest {
  line-height: 1.3;
}

/* Thanh áp dụng đề xuất hàng loạt (thay nút "Áp dụng" từng dòng). */
.rf-suggest-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #fff4de;
  border: 1px solid #ffa800;
  border-radius: 0.42rem;
  color: #3f4254;
  font-size: 12px;
  line-height: 1.4;
}

.rf-suggest-text {
  flex: 1 1 260px;
}

.rf-suggest-text .fa-lightbulb {
  color: #ffa800;
}

.rf-suggest-apply.btn {
  flex: 0 0 auto;
  white-space: nowrap;
}

.rf-lookup-btn.btn i {
  padding: 0 !important;
  margin: 0 !important;
}

.rf-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebedf3;
}

.rf-actions .btn i {
  font-size: 1rem;
  padding-right: 0.35rem;
}
</style>

<!-- KHÔNG scoped: b-modal render ra ngoài body nên dialog-class không nhận style scoped.
     Nới rộng modal vượt giới hạn 1140px của size="xl" để bảng vừa khít, hết cuộn ngang. -->
<style>
/* Theme khóa .modal-dialog.modal-xl ở 1100px (xl − 100). */
.modal-dialog.modal-xl.review-fast-modal-dialog {
  width: auto !important;
  max-width: 95vw !important;
}

body.rf-email-filling,
body.rf-email-filling * {
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Cpath d='M9 1h2v18H9zM1 9h18v2H1z' fill='%23000'/%3E%3C/svg%3E")
      10 10,
    cell !important;
}
</style>
